import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  CalendarBlank,
  MapPin,
  Microphone,
  CoffeeBean,
  CheckCircle,
} from "@phosphor-icons/react";
import { Reveal, Stagger, fadeItem } from "@/components/site/Reveal";
import SpeakerCard from "@/components/site/SpeakerCard";
import {
  FAMILY_BUSINESS_CONCLAVE as EVENT,
  SPEAKERS,
  formatINR,
} from "@/lib/events";

// All unique speaker slugs across the agenda, in agenda order, deduplicated.
function uniqueSpeakerSlugs(sessions) {
  const seen = new Set();
  const out = [];
  for (const s of sessions) {
    if (s.type === "break") continue;
    for (const sp of s.speakers || []) {
      if (!seen.has(sp)) { seen.add(sp); out.push(sp); }
    }
    if (s.moderator && !seen.has(s.moderator)) {
      seen.add(s.moderator);
      out.push(s.moderator);
    }
  }
  return out;
}

export default function FamilyBusinessConclave() {
  const { search } = useLocation();
  const initialTier = useMemo(() => {
    const params = new URLSearchParams(search);
    const t = params.get("tier");
    return EVENT.tiers.find((x) => x.id === t)?.id || "";
  }, [search]);

  const speakerSlugs = useMemo(() => uniqueSpeakerSlugs(EVENT.sessions), []);

  return (
    <main data-testid="fbc-page" className="bg-parchment text-ocean">
      <Hero />
      <About />
      <Agenda />
      <Speakers slugs={speakerSlugs} />
      <Venue />
      <Tiers />
      <RegistrationSection initialTier={initialTier} />
      <ContactCTA />
    </main>
  );
}

/* ------------------------- Hero ------------------------- */
function Hero() {
  return (
    <section className="relative overflow-hidden text-parchment min-h-[80vh] flex items-end pt-32 pb-16 md:pt-40 md:pb-24">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${EVENT.heroImage})` }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-ocean via-ocean/80 to-ocean/30"
        aria-hidden="true"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="overline text-brass">{EVENT.dateOverline}</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl md:text-8xl mt-6 leading-[0.9] max-w-4xl">
          {EVENT.title}
          <br />
          <em className="text-brass">{EVENT.tagline}</em>
        </h1>
        <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-parchment/80">
          <div className="flex items-center gap-2">
            <CalendarBlank size={20} weight="light" className="text-brass" /> {EVENT.date}
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={20} weight="light" className="text-brass" /> {EVENT.venue}
          </div>
        </div>
        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href="#register"
            data-testid="fbc-hero-cta-register"
            className="overline px-6 py-4 bg-brass text-ocean hover:bg-parchment transition-colors flex items-center gap-2"
          >
            Register now <ArrowUpRight size={16} weight="bold" />
          </a>
          <a
            href="#agenda"
            data-testid="fbc-hero-cta-agenda"
            className="overline px-6 py-4 border border-parchment/40 text-parchment hover:bg-parchment/10 transition-colors flex items-center gap-2"
          >
            View agenda
          </a>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- About ------------------------- */
function About() {
  return (
    <section className="py-20 md:py-28 border-b border-black/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="overline text-spice">About</div>
          <div className="divider-brass mt-6" />
        </div>
        <div className="md:col-span-8">
          <Reveal>
            <p className="font-serif-display text-3xl md:text-5xl leading-tight">
              {EVENT.about}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Agenda ------------------------- */
function Agenda() {
  return (
    <section id="agenda" className="py-20 md:py-28 bg-parchment-deep border-b border-black/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <div className="overline text-spice">Agenda</div>
          <h2 className="font-serif-display text-4xl md:text-6xl mt-4">
            Four conversations, <em className="text-spice">one afternoon.</em>
          </h2>
        </div>

        <Stagger className="space-y-4">
          {EVENT.sessions.map((s, i) => {
            if (s.type === "break") {
              return (
                <motion.div
                  key={s.id}
                  variants={fadeItem}
                  data-testid={`agenda-row-${s.id}`}
                  className="flex items-center gap-4 py-5 px-6 border border-dashed border-black/20"
                >
                  <CoffeeBean size={22} weight="light" className="text-spice" />
                  <div className="overline text-ocean/60">{s.title}</div>
                </motion.div>
              );
            }
            const speakers = (s.speakers || []).map((slug) => SPEAKERS[slug]).filter(Boolean);
            const moderator = s.moderator ? SPEAKERS[s.moderator] : null;
            return (
              <motion.article
                key={s.id}
                variants={fadeItem}
                data-testid={`agenda-row-${s.id}`}
                className="grid md:grid-cols-12 gap-6 md:gap-8 p-6 md:p-8 bg-parchment border border-black/10 hover:border-spice transition-colors"
              >
                <div className="md:col-span-1 font-serif text-3xl text-brass">
                  {String(agendaIndex(i)).padStart(2, "0")}
                </div>
                <div className="md:col-span-11">
                  <h3 className="font-serif text-2xl md:text-3xl leading-snug">{s.title}</h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {speakers.map((sp) => (
                      <span
                        key={sp.name}
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] px-3 py-1.5 bg-spice/10 text-spice border border-spice/20"
                      >
                        <Microphone size={12} weight="fill" /> {sp.name}
                      </span>
                    ))}
                    {moderator && (
                      <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] px-3 py-1.5 bg-brass/10 text-brass border border-brass/30">
                        Moderator · {moderator.name}
                      </span>
                    )}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
}

// Counts only non-break rows so the displayed session number ignores the tea break.
function agendaIndex(i) {
  let n = 0;
  for (let k = 0; k <= i; k++) if (EVENT.sessions[k].type !== "break") n++;
  return n;
}

/* ------------------------- Speakers ------------------------- */
function Speakers({ slugs }) {
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <div className="overline text-spice">Speakers &amp; moderators</div>
          <h2 className="font-serif-display text-4xl md:text-6xl mt-4">
            The people on stage.
          </h2>
          <p className="text-ocean/60 text-sm mt-4 italic">
            Bios and portraits are being finalised — placeholders for now.
          </p>
        </div>

        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {slugs.map((slug) => (
            <motion.div key={slug} variants={fadeItem}>
              <SpeakerCard speaker={SPEAKERS[slug]} />
            </motion.div>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ------------------------- Venue ------------------------- */
function Venue() {
  return (
    <section className="py-20 md:py-28 bg-ocean text-parchment relative grain overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <div className="overline text-brass">Venue</div>
          <h2 className="font-serif-display text-5xl md:text-7xl mt-4 leading-[0.95]">
            {EVENT.venue.split(",")[0]}.
          </h2>
          <p className="mt-6 text-parchment/75 leading-relaxed max-w-xl">
            A central, well-connected hall in the heart of Kozhikode. Parking, accessibility and
            wi-fi all sorted. Final directions will be sent to registered attendees a week before
            the event.
          </p>
        </div>
        <div className="md:col-span-5 border border-white/15 p-8 md:p-10 bg-white/5">
          <div className="overline text-brass mb-3">Date</div>
          <div className="font-serif text-3xl md:text-4xl">{EVENT.date}</div>
          <div className="divider-brass my-6" />
          <div className="overline text-brass mb-3">Venue</div>
          <div className="font-serif text-2xl">{EVENT.venue}</div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Tiers ------------------------- */
function Tiers() {
  return (
    <section className="py-20 md:py-28 border-b border-black/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <div className="overline text-spice">Tickets</div>
          <h2 className="font-serif-display text-4xl md:text-6xl mt-4">
            Pick your <em className="text-spice">tier.</em>
          </h2>
          <p className="text-ocean/60 text-sm mt-4 italic">
            Prices below are placeholders pending final pricing approval.
          </p>
        </div>

        <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {EVENT.tiers.map((t) => (
            <motion.article
              key={t.id}
              variants={fadeItem}
              data-testid={`tier-card-${t.id}`}
              className="flex flex-col border border-black/10 bg-parchment p-8 hover:border-spice transition-colors"
            >
              <div className="overline text-spice">{t.label}</div>
              <div className="font-serif text-5xl md:text-6xl mt-4 text-ocean">
                {formatINR(t.priceINR)}
              </div>
              <p className="mt-4 text-ocean/70 text-sm leading-relaxed flex-1">{t.description}</p>
              <a
                href={`#register?tier=${t.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  // Update URL search param without a full navigation so the
                  // form picks up `?tier=` on the next render.
                  const url = new URL(window.location.href);
                  url.searchParams.set("tier", t.id);
                  window.history.replaceState({}, "", url.toString());
                  // Notify the form to re-read tier from the URL.
                  window.dispatchEvent(new CustomEvent("fbc:tier", { detail: t.id }));
                  document.getElementById("register")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                data-testid={`tier-cta-${t.id}`}
                className="overline mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 bg-ocean text-parchment hover:bg-spice transition-colors"
              >
                Register as {t.label.toLowerCase()} <ArrowUpRight size={14} weight="bold" />
              </a>
            </motion.article>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ------------------------- Registration ------------------------- */
function RegistrationSection({ initialTier }) {
  const [tier, setTier] = useState(initialTier);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    designation: "",
    sector: "",
    dietary: "",
  });

  // Listen for tier-card clicks so they pre-select the tier on this form.
  useEffect(() => {
    const handler = (e) => setTier(e.detail);
    window.addEventListener("fbc:tier", handler);
    return () => window.removeEventListener("fbc:tier", handler);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email and phone.");
      return;
    }
    if (!tier) {
      toast.error("Pick a ticket tier above before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`/api/events/${EVENT.slug}/register`, { ...form, tier });
      setSuccess({ id: res.data?.id });
      toast.success("Registered! Payment details coming to your inbox soon.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-ocean/20 focus:border-ocean focus:outline-none py-3 text-lg placeholder:text-ocean/40 transition-colors";

  return (
    <section id="register" className="py-20 md:py-28 bg-parchment-deep border-b border-black/10 scroll-mt-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="mb-10">
          <div className="overline text-spice">Register</div>
          <h2 className="font-serif-display text-4xl md:text-6xl mt-4">
            Save your <em className="text-spice">seat.</em>
          </h2>
          <p className="text-ocean/70 mt-4 leading-relaxed">
            We'll capture your details here. Payment instructions will follow over email once the
            gateway goes live — your spot is held in the meantime.
          </p>
        </div>

        {success ? (
          <div
            data-testid="fbc-success-card"
            className="border border-leaf/30 bg-leaf/5 p-8 md:p-12 flex items-start gap-5"
          >
            <CheckCircle size={32} weight="light" className="text-leaf shrink-0" />
            <div>
              <h3 className="font-serif text-3xl">You're on the list.</h3>
              <p className="text-ocean/75 mt-3 leading-relaxed">
                Registration <span className="font-mono">#{success.id}</span> confirmed.
                We'll email payment instructions and the final venue brief shortly.
              </p>
              <p className="text-ocean/55 text-sm mt-4 italic">
                If you don't see anything within 48 hours, write to{" "}
                <a className="text-spice underline" href="mailto:kozhikode@youngindians.net">
                  kozhikode@youngindians.net
                </a>
                .
              </p>
            </div>
          </div>
        ) : (
          <motion.form
            data-testid="fbc-register-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-10"
          >
            {/* Tier picker */}
            <div>
              <div className="overline text-ocean/50 mb-3">Ticket tier *</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {EVENT.tiers.map((t) => {
                  const selected = tier === t.id;
                  return (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => setTier(t.id)}
                      data-testid={`fbc-tier-btn-${t.id}`}
                      className={`text-left p-4 border transition-colors ${
                        selected
                          ? "border-spice bg-spice/5"
                          : "border-ocean/15 hover:border-ocean/40"
                      }`}
                    >
                      <div className="font-serif text-2xl">{t.label}</div>
                      <div className="overline text-ocean/60 mt-1">{formatINR(t.priceINR)}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              <label className="block">
                <div className="overline text-ocean/50 mb-2">Your name *</div>
                <input
                  data-testid="fbc-input-name"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Aiswarya Menon"
                  className={inputCls}
                />
              </label>
              <label className="block">
                <div className="overline text-ocean/50 mb-2">Email *</div>
                <input
                  data-testid="fbc-input-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@calicut.in"
                  className={inputCls}
                />
              </label>
              <label className="block">
                <div className="overline text-ocean/50 mb-2">Phone *</div>
                <input
                  data-testid="fbc-input-phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 ..."
                  className={inputCls}
                />
              </label>
              <label className="block">
                <div className="overline text-ocean/50 mb-2">Company / Organisation</div>
                <input
                  data-testid="fbc-input-company"
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Acme Family Holdings"
                  className={inputCls}
                />
              </label>
              <label className="block">
                <div className="overline text-ocean/50 mb-2">Designation</div>
                <input
                  data-testid="fbc-input-designation"
                  value={form.designation}
                  onChange={update("designation")}
                  placeholder="Director / Founder / Student"
                  className={inputCls}
                />
              </label>
              <label className="block">
                <div className="overline text-ocean/50 mb-2">Sector</div>
                <input
                  data-testid="fbc-input-sector"
                  value={form.sector}
                  onChange={update("sector")}
                  placeholder="e.g. Manufacturing, Retail, Hospitality"
                  className={inputCls}
                />
              </label>
            </div>

            {/* Dietary */}
            <div>
              <div className="overline text-ocean/50 mb-3">Dietary preference</div>
              <div className="flex gap-3 flex-wrap">
                {["veg", "non-veg"].map((d) => {
                  const selected = form.dietary === d;
                  return (
                    <button
                      type="button"
                      key={d}
                      onClick={() => setForm((f) => ({ ...f, dietary: d }))}
                      data-testid={`fbc-dietary-${d}`}
                      className={`overline px-5 py-3 border transition-colors ${
                        selected
                          ? "border-spice bg-spice text-parchment"
                          : "border-ocean/20 text-ocean hover:border-ocean"
                      }`}
                    >
                      {d === "veg" ? "Vegetarian" : "Non-vegetarian"}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting}
                data-testid="fbc-submit"
                className="overline px-8 py-5 bg-ocean text-parchment hover:bg-spice disabled:opacity-60 transition-colors inline-flex items-center gap-2"
              >
                {submitting ? "Saving…" : "Confirm registration"} <ArrowUpRight size={16} weight="bold" />
              </button>
              <p className="text-ocean/50 text-xs mt-4">
                By registering you agree to receive event-related emails from Yi Kozhikode. We do not
                share registrant data with third parties.
              </p>
            </div>
          </motion.form>
        )}
      </div>
    </section>
  );
}

/* ------------------------- Contact CTA ------------------------- */
function ContactCTA() {
  return (
    <section className="py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <div className="overline text-spice">Questions?</div>
        <p className="font-serif-display text-3xl md:text-4xl mt-4">
          Email{" "}
          <a className="text-spice underline" href="mailto:kozhikode@youngindians.net">
            kozhikode@youngindians.net
          </a>{" "}
          — we answer everyone.
        </p>
      </div>
    </section>
  );
}
